"use client";

import Link from "next/link";
import { ArrowRight, KeyRound, LoaderCircle, ShieldCheck, UserRound } from "lucide-react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { AuthHeading, AuthLayout, AuthNote, StatusPanel } from "@/components/auth-ui";
import { FormField } from "@/components/facility-onboarding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getErrorCopy } from "@/lib/error-config";
import { OperationError, callOperation } from "@/lib/f360-client";

/* Standard password recovery: the user provides their username (or email)
   and the backend emails its normal reset link, addressed to the
   in-app /update-password page. The confirmation is uniform whether or
   not the account exists. */
export default function RecoverAccessPage() {
  const router = useRouter();
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const value = username.trim();
    if (!value) { setError("Enter your username or email."); return; }
    setError(""); setLoading(true);
    try {
      await callOperation("auth.request-password-reset", { user: value });
      router.push("/recover-access/sent");
    } catch (requestError) {
      // 429 is IP-scoped (nothing about the account leaks), so it gets an
      // honest message. Anything else below 500 means the request was
      // handled (unknown or disabled accounts answer uniformly) — keep
      // the reply uniform and continue to the confirmation page.
      if (requestError instanceof OperationError && requestError.status === 429) {
        setError(getErrorCopy("RATE_LIMITED").message); setLoading(false);
      } else if (requestError instanceof OperationError && requestError.status < 500) {
        router.push("/recover-access/sent");
      } else {
        setError(getErrorCopy("RECOVERY_FAILED").message); setLoading(false);
      }
    }
  };
  return <AuthLayout variant="sign-in" size="narrow">
    <div className="auth-icon-tile" aria-hidden="true"><KeyRound size={26} /></div>
    <AuthHeading title="Reset your password">Enter the username or email for your facility account. We’ll email you a link to choose a new password.</AuthHeading>
    <form className="auth-stack" onSubmit={submit} noValidate>
      <FormField id="recovery-username" label="Username or email" required icon={UserRound}>
        <Input id="recovery-username" autoComplete="username" value={username} onChange={(event) => { setUsername(event.target.value); setError(""); }} placeholder="Enter your username or email" aria-invalid={Boolean(error)} autoFocus />
      </FormField>
      {error && <StatusPanel tone="danger" title="Link not sent">{error}</StatusPanel>}
      <Button type="submit" disabled={loading}>{loading ? <><LoaderCircle size={16} className="spin" />Sending link…</> : <>Send reset link <ArrowRight size={16} /></>}</Button>
      <Link className="auth-text-link" href="/sign-in">Back to sign in</Link>
    </form>
    <AuthNote icon={ShieldCheck}>For your privacy, we show the same message whether or not an account matches these details.</AuthNote>
  </AuthLayout>;
}
